/**
 * BlocksTemplate — renderiza o santinho a partir da lista de Block[].
 *
 * Ativa automaticamente quando publicPage.blocks contem entradas. Para
 * tenants legados (sem blocks), a SantinhoPage continua usando os templates
 * v1 (Classic/Modern/Bold/Minimal/Campaign).
 */

import { BlockRenderer } from '../blocks/BlockRenderer';
import { resolveBlocks } from '../blocks/hydrate';
import { ContactRegistrationButton } from '../ContactRegistrationButton';
import { WebchatWidget } from '../../webchat';
import { Logo } from '../../Logo';
import { useGoogleFonts } from '@/hooks/useGoogleFonts';
import { getTypographyPreset } from '@/config/santinho-design-tokens';
import type { SantinhoTemplateProps } from './types';

export function BlocksTemplate({ tenant }: SantinhoTemplateProps) {
  const blocks = resolveBlocks(tenant.publicPage);
  const theme = tenant.theme;
  const typo = getTypographyPreset(theme.typographyPreset);
  useGoogleFonts(typo?.fontUrls);
  const fontBody = theme.fontBody || theme.fontFamily || 'inherit';

  return (
    <div
      className="min-h-screen bg-white"
      style={{
        fontFamily: fontBody,
        // CSS variables locais para os blocos consumirem se precisar
        ['--santinho-primary' as never]: theme.primaryColor,
        ['--santinho-secondary' as never]: theme.secondaryColor,
        ['--santinho-accent' as never]: theme.accentColor || `${theme.primaryColor}15`,
        ['--santinho-font-display' as never]: theme.fontDisplay || fontBody,
        ['--santinho-font-body' as never]: fontBody,
      }}
    >
      <main>
        <BlockRenderer blocks={blocks} ctx={{ tenant, isEditor: false }} />
      </main>

      <footer className="py-8 mt-4" style={{ backgroundColor: theme.secondaryColor }}>
        <div className="container mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
            <Logo size={18} />
            <span className="text-xs font-semibold text-white/80">Powered by CampanhaFlow</span>
          </div>
        </div>
      </footer>

      <ContactRegistrationButton />
      <WebchatWidget tenant={tenant} />
    </div>
  );
}
